// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminOrgScope } from '@/hooks/useAdminOrgScope';

interface EventCategory {
  id: string;
  name: string;
  created_at?: string;
}

const EventCategoriesManagement: React.FC = () => {
  const { scopeOrgId, createOrgId, ready } = useAdminOrgScope();
  const [eventCategories, setEventCategories] = useState<EventCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEventCategory, setEditingEventCategory] = useState<EventCategory | null>(null);
  const [newEventCategory, setNewEventCategory] = useState({ name: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!ready) return;
    fetchEventCategories();
  }, [ready, scopeOrgId]);

  const fetchEventCategories = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('event_categories')
        .select('id, name, created_at')
        .order('name');

      // Org admin -> only their categories; super admin -> all categories.
      if (scopeOrgId) query = query.eq('organization_id', scopeOrgId);

      const { data, error } = await query;

      if (error) throw error;
      setEventCategories(data || []);
    } catch (err) {
      console.error('Error fetching event categories:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEventCategory = async () => {
    if (!newEventCategory.name.trim()) {
      toast.error('Event category name is required');
      return;
    }

    try {
      setSubmitting(true);
      const { error } = await supabase
        .from('event_categories')
        .insert([{
          name: newEventCategory.name.trim(),
          organization_id: createOrgId
        }]);

      if (error) throw error;

      toast.success('Event category created successfully');
      setIsCreateDialogOpen(false);
      setNewEventCategory({ name: '' });
      fetchEventCategories();
    } catch (err) {
      console.error('Error creating event category:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create event category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateEventCategory = async () => {
    if (!editingEventCategory || !editingEventCategory.name.trim()) {
      toast.error('Event category name is required');
      return;
    }

    try {
      setSubmitting(true);
      const { error } = await supabase
        .from('event_categories')
        .update({
          name: editingEventCategory.name.trim()
        })
        .eq('id', editingEventCategory.id);

      if (error) throw error;

      toast.success('Event category updated successfully');
      setIsEditDialogOpen(false);
      setEditingEventCategory(null);
      fetchEventCategories();
    } catch (err) {
      console.error('Error updating event category:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update event category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEventCategory = async (eventCategory: EventCategory) => {
    try {
      const { error } = await supabase
        .from('event_categories')
        .delete()
        .eq('id', eventCategory.id);

      if (error) throw error;

      toast.success('Event category deleted successfully');
      fetchEventCategories();
    } catch (err) {
      console.error('Error deleting event category:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete event category');
    }
  };

  const openEditDialog = (eventCategory: EventCategory) => {
    setEditingEventCategory({ ...eventCategory });
    setIsEditDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading event categories...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-red-600">Error loading event categories: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div />
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Event Category
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Event Category</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Event Category Name</Label>
                <Input
                  id="name"
                  value={newEventCategory.name}
                  onChange={(e) => setNewEventCategory({ ...newEventCategory, name: e.target.value })}
                  placeholder="Enter event category name"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateEventCategory} disabled={submitting}>
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Create Event Category
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Event Categories ({eventCategories.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {eventCategories.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eventCategories.map((eventCategory) => (
                  <TableRow key={eventCategory.id}>
                    <TableCell className="font-medium">
                      {eventCategory.name}
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {eventCategory.created_at ? new Date(eventCategory.created_at).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(eventCategory)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Event Category</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{eventCategory.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteEventCategory(eventCategory)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No event categories found</p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create your first event category
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Event Category</DialogTitle>
          </DialogHeader>
          {editingEventCategory && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Event Category Name</Label>
                <Input
                  id="edit-name"
                  value={editingEventCategory.name}
                  onChange={(e) => setEditingEventCategory({ ...editingEventCategory, name: e.target.value })}
                  placeholder="Enter event category name"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateEventCategory} disabled={submitting}>
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Update Event Category
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventCategoriesManagement;
